import React from 'react';
import { PawPrint, ShieldCheck, Sparkles, Heart, MapPin } from 'lucide-react';
import PageShell from '@/components/PageShell';
import SeoMeta, { breadcrumbJsonLd } from '@/components/SeoMeta';
import { useSignup } from '@/lib/SignupContext';
import { useLang } from '@/lib/LangContext';
import ConsultationDialog from '@/components/ConsultationDialog';

/*
 * Bilingual copy. Kept in a single COPY tree so the About page stays in
 * perfect EN/KA parity without threading a dozen keys through i18n.js.
 */
const COPY = {
  seoTitle_en: 'About — SmartPaw Food, Tbilisi',
  seoTitle_ka: 'ჩვენ შესახებ — SmartPaw Food, თბილისი',
  seoDesc_en:
    'SmartPaw Food — simplify responsible pet care without raising the bill. Founded in 2025 by a team of pet-care experts in Tbilisi.',
  seoDesc_ka:
    'SmartPaw Food — ცხოველის პასუხისმგებლიანი მოვლის გამარტივება დამატებითი ხარჯების გარეშე. დაფუძნებულია 2025 წელს თბილისში, ცხოველთა მოვლის ექსპერტების გუნდის მიერ.',
  breadcrumbHome_en: 'Home',
  breadcrumbHome_ka: 'მთავარი',
  breadcrumbAbout_en: 'About',
  breadcrumbAbout_ka: 'ჩვენ შესახებ',

  shellEyebrow_en: 'About',
  shellEyebrow_ka: 'ჩვენ შესახებ',
  shellTitle_en: 'SmartPaw Food — simplify with no extra cost.',
  shellTitle_ka: 'SmartPaw Food — გაიმარტივე საქმე დამატებითი ხარჯების გარეშე.',
  shellIntro_en:
    'SmartPaw was incorporated in 2025 by a team of pet-care experts. Our main goal is to simplify and raise the quality of responsible pet care, without increasing expenses for pet owners. Our first service is the regular supply of products to households — so you never have to visit a pet store again. But we are not planning to stop there: we are introducing new initiatives and innovations across active lifestyle, technology, information and other areas.',
  shellIntro_ka:
    'SmartPaw დაფუძნდა 2025 წელს ცხოველთა მოვლის ექსპერტების გუნდის მიერ. ჩვენი მთავარი მიზანია ცხოველის პასუხისმგებლიანი მოვლის გამარტივება და ხარისხის ამაღლება, ოთხფეხების მფლობელების ხარჯების ზრდის გარეშე. ჩვენი პირველი სერვისი — ოჯახებისთვის პროდუქტების რეგულარული მიწოდებაა, რათა მათ აღარ მოუწიოთ ზოომაღაზიებში სიარული. მაგრამ არ ვჩერდებით ამით — ვამზადებთ ბევრ ახალ ინიციატივასა და ინოვაციას აქტიური ცხოვრების სტილის, ტექნოლოგიების, ინფორმაციისა და სხვა მიმართულებებით.',
  shellImageAlt_en: 'A SmartPaw delivery box',
  shellImageAlt_ka: 'SmartPaw-ის მიწოდების ყუთი',

  founderEyebrow_en: 'Founder story',
  founderEyebrow_ka: 'დამფუძნებლის ისტორია',
  founderTitle_en: 'Built for pet parents, by one of them.',
  founderTitle_ka: 'შექმნილია ოთხფეხების პატრონებისთვის — ერთი მათგანის მიერ.',
  founderPara1_a_en:
    'Guga, the main founder of SmartPaw, has two pets — a golden retriever named ',
  founderPara1_a_ka:
    'გუგას, SmartPaw-ის მთავარ დამფუძნებელს, ჰყავს ორი ოთხფეხა — ოქროსფერი რეტრივერი ',
  founderPara1_b_en: ' and a rescued stray cat named ',
  founderPara1_b_ka: ' და მიშვილებული კატა ',
  founderPara1_c_en:
    '. Like every pet owner, Guga found caring for them harder than it needed to be.',
  founderPara1_c_ka:
    '. ყველა პატრონის მსგავსად, გუგასაც ეჩოთიროდა, რომ მათი მოვლა საჭიროზე რთული იყო.',
  founderPara2_en:
    'Coming back from work and realising there was no food or essential products at home. Forgetting when to book medical check-ups or give medicine. Parking near busy pet stores and carrying heavy bags up the stairs — you name it.',
  founderPara2_ka:
    'სამსახურიდან დაბრუნებისას გახსენება, რომ სახლში არ არის საჭირო პროდუქტები. დავიწყება, თუ როდის არის სამედიცინო შემოწმებისა თუ მედიკამენტების დრო. ზოომაღაზიებთან მანქანის დაპარკვა და მძიმე ტომრების ტარება სართულებზე — ყველაფერი ეს ნაცნობი ფაქტებია.',
  founderPara3_lead_en: 'So Guga decided to solve the problem in a modern, effective way.',
  founderPara3_lead_ka:
    'ამიტომ გუგამ გადაწყვიტა, ეს პრობლემა თანამედროვე და ეფექტური გზით გადაეჭრა.',
  founderPara3_bold_en: 'That is how SmartPaw was born.',
  founderPara3_bold_ka: 'ასე დაიბადა SmartPaw.',

  valuesEyebrow_en: 'What we stand for',
  valuesEyebrow_ka: 'რაში ვართ დარწმუნებული',
  valuesTitle_en: "Four things we don't compromise on.",
  valuesTitle_ka: 'ოთხი რამ, რაშიც კომპრომისს არ ვუშვებთ.',

  teamEyebrow_en: 'The team',
  teamEyebrow_ka: 'გუნდი',
  teamTitle_en: 'A founder, an expert and two four-legged inspectors.',
  teamTitle_ka: 'დამფუძნებელი, ექსპერტი და ორი ოთხფეხა ინსპექტორი.',
  teamIntro_en:
    'Our team is small by design — one founder, one pet-care expert and two full-time Quality Control officers who happen to walk on four legs.',
  teamIntro_ka:
    'ჩვენი გუნდი განზრახ მცირეა — ერთი დამფუძნებელი, ერთი ცხოველთა მოვლის ექსპერტი და ორი სრული განაკვეთის ხარისხის კონტროლის ოფიცერი, რომლებიც ოთხ ფეხზე დადიან.',
};

const VALUES = [
  {
    key: 'vet',
    icon: ShieldCheck,
    title_en: 'Vet-aligned',
    title_ka: 'ვეტერინარების ნდობით',
    body_en:
      "Every brand on the shelf is one a Tbilisi vet would recommend — no diet fads, no gimmicks, no 'grain-free because it sounds clean'.",
    body_ka:
      'თითოეული ბრენდი შერჩეულია ვეტერინარების რეკომენდაციით — არანაირი დიეტური მოდა, ხრიკი ან "უმარცვლო, რადგან სუფთად ჟღერს" მიდგომა.',
  },
  {
    key: 'pricing',
    icon: Heart,
    title_en: 'Honest pricing',
    title_ka: 'გულწრფელი ფასები',
    body_en:
      'No hidden fees, no minimum-order tax, no fuel surcharge. The price you sign up at is the price you keep.',
    body_ka:
      'არანაირი დამალული გადასახადი, მინიმალური შეკვეთის გადასახადი ან საწვავის დანამატი. რეგისტრაციისას ნანახი ფასი ზუსტად ის ფასია, რომელსაც გადაიხდი.',
  },
  {
    key: 'tbilisi',
    icon: MapPin,
    title_en: 'Tbilisi roots',
    title_ka: 'ფესვებით თბილისი',
    body_en:
      'Locally founded, locally delivered. We answer the WhatsApp, we know the districts, and we know your dog by name on the second message.',
    body_ka:
      'ადგილობრივად დაფუძნებული, ადგილობრივად მომარაგებული. WhatsApp-ზე ჩვენ ვპასუხობთ, უბნებს კარგად ვიცნობთ და მეორე მესიჯიდან უკვე სახელით ვიცნობთ შენს ოთხფეხას.',
  },
  {
    key: 'modern',
    icon: Sparkles,
    title_en: 'Quietly modern',
    title_ka: 'მშვიდი თანამედროვეობა',
    body_en:
      'A free SmartPaw Feeder, a self-managed schedule and a team that actually picks up. Modern enough to feel effortless, calm enough to stay invisible.',
    body_ka:
      'უფასო SmartPaw ფიდერი, თვითმართვადი გრაფიკი და გუნდი, რომელიც მართლაც პასუხობს. საკმარისად თანამედროვე, რომ არც გაგრძნობინო და საკმარისად მშვიდი, რომ ხელი არ შეგიშალო.',
  },
];

/*
 * Founding team per PO. Photos cropped from the Georgian investor deck
 * (last page) and served from /public/team/. Bios are rendered dynamically
 * depending on the active locale.
 */
const TEAM = [
  {
    key: 'guga',
    name_en: 'Guga Abashidze',
    name_ka: 'გუგა აბაშიძე',
    role_en: 'Founder & CEO',
    role_ka: 'დამფუძნებელი და აღმასრულებელი დირექტორი',
    bio_en:
      "Guga leads SmartPaw's strategy, partnerships and day-to-day operations. A lifelong pet parent to Simba and Appa, he started SmartPaw so pet care in Tbilisi could finally feel calm, honest and modern.",
    bio_ka:
      'გუგა ხელმძღვანელობს SmartPaw-ის სტრატეგიას, პარტნიორობებსა და ყოველდღიურ ოპერაციებს. მას ჰყავს ოთხფეხა ოჯახი — სიმბა და აპა — სწორედ ამან დააბადა იდეა, რომ ცხოველის მოვლა თბილისში უფრო მარტივი, გულწრფელი და თანამედროვე გახდეს.',
    image: '/team/guga.jpg',
  },
  {
    key: 'giorgi',
    name_en: 'Giorgi Kobakhidze',
    name_ka: 'გიორგი კობახიძე',
    role_en: 'Cynologist & Pet-Care Expert',
    role_ka: 'კინოლოგი და ცხოველთა მოვლის ექსპერტი',
    bio_en:
      'Experienced nutritionist and founder of the digital platform Dogography. Giorgi helps every member pick the right products, dial in portions and keep each pet on the right routine.',
    bio_ka:
      'გამოცდილი ნუტრიციონისტი, ციფრული პლატფორმა — Dogography-ის დამფუძნებელი. გიორგი დაგვეხმარება სწორი პროდუქტების არჩევაში, ულუფების გაწერასა და თითოეული ცხოველის რუტინის დახვეწაში.',
    image: '/team/giorgi.jpg',
  },
  {
    key: 'mark',
    name_en: 'Mark Donavon',
    name_ka: 'მარკ დონავონი',
    role_en: 'Senior Advisor & Supervisor',
    role_ka: 'მთავარი მრჩეველი და ზედამხედველი',
    bio_en:
      'Nestlé Purina veteran with 25+ years in the industry, 360-degree pet-nutrition patents and CEO of Launchpad Pet. Brings SmartPaw global expertise and category credibility.',
    bio_ka:
      'Nestlé Purina-ს ვეტერანი, 25+ წლიანი გამოცდილებით, 360° პატენტები და Launchpad Pet-ის CEO. SmartPaw-ს აძლევს გლობალურ ექსპერტიზასა და დარგობრივ სანდოობას.',
    image: '/team/mark.jpg',
  },
  {
    key: 'simba',
    name_en: 'Simba',
    name_ka: 'სიმბა',
    role_en: 'Quality Control',
    role_ka: 'ხარისხის კონტროლი',
    bio_en:
      "Guga's golden retriever and SmartPaw's resident taste-tester. If Simba doesn't wag through the sampling, the product doesn't make the catalogue — simple as that.",
    bio_ka:
      'გუგას ოქროსფერი რეტრივერი და SmartPaw-ის მთავარი დეგუსტატორი. თუ სიმბა ლაგამს არ გაუქნია პროდუქტს — კატალოგში არ ხვდება. ასე მარტივად.',
    image: '/team/simba.jpg',
  },
];

const METRICS = [
  {
    key: 'pets',
    value: '1,000+',
    label_en: 'Pets supplied per month',
    label_ka: 'მომარაგებული ოთხფეხა თვეში',
  },
  {
    key: 'catalogue',
    value: '500+',
    label_en: 'Products in catalogue',
    label_ka: 'პროდუქტი კატალოგში',
  },
  {
    key: 'late',
    value: '< 8',
    label_en: 'Late deliveries per month',
    label_ka: 'დაგვიანებული მიწოდება თვეში',
  },
  {
    key: 'fee',
    value: '0 GEL',
    label_en: 'Service fee',
    label_ka: 'მომსახურების გადასახადი',
  },
];

export default function AboutPage() {
  const { openSignup } = useSignup();
  const { lang } = useLang();
  // Suffix used to pick between `_en` / `_ka` fields across all data objects.
  const L = lang === 'ka' ? '_ka' : '_en';
  const t = (key) => COPY[`${key}${L}`];
  const [consultOpen, setConsultOpen] = React.useState(false);

  return (
    <>
      <SeoMeta
        title={t('seoTitle')}
        description={t('seoDesc')}
        jsonLd={breadcrumbJsonLd([
          { name: t('breadcrumbHome'), path: '/' },
          { name: t('breadcrumbAbout'), path: '/about' },
        ])}
      />
      <PageShell
        eyebrow={t('shellEyebrow')}
        title={t('shellTitle')}
        intro={t('shellIntro')}
        image="https://images.pexels.com/photos/8434670/pexels-photo-8434670.jpeg?auto=compress&cs=tinysrgb&w=1400"
        imageAlt={t('shellImageAlt')}
      >
        {/* Founder story */}
        <section
          className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
          data-testid="about-founder-section"
        >
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#F25C05]">
              {t('founderEyebrow')}
            </p>
            <h2 className="font-display font-bold text-[#05223D] text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.02] mt-3">
              {t('founderTitle')}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-5 text-[#465B70] text-lg leading-relaxed">
            <p>
              {t('founderPara1_a')}
              <span className="font-bold text-[#05223D]">
                {lang === 'ka' ? 'სიმბა' : 'Simba'}
              </span>
              {t('founderPara1_b')}
              <span className="font-bold text-[#05223D]">
                {lang === 'ka' ? 'აპა' : 'Appa'}
              </span>
              {t('founderPara1_c')}
            </p>
            <p>{t('founderPara2')}</p>
            <p>
              {t('founderPara3_lead')}{' '}
              <span className="font-bold text-[#05223D]">{t('founderPara3_bold')}</span>
            </p>
          </div>
        </section>

        {/* Impact metrics */}
        <section className="mt-20 md:mt-28" data-testid="about-metrics-section">
          <div className="rounded-[2rem] bg-[#0A4D8C] text-white px-6 py-10 md:px-12 md:py-14 relative overflow-hidden">
            <div className="absolute inset-0 grain pointer-events-none" aria-hidden />
            <div
              className="absolute -top-32 -right-24 w-[360px] h-[360px] rounded-full bg-[#F25C05]/25 blur-3xl pointer-events-none"
              aria-hidden
            />
            <div className="relative grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              {METRICS.map((m) => (
                <div key={m.key} data-testid={`about-metric-${m.key}`}>
                  <p className="font-display font-extrabold text-4xl md:text-5xl tracking-[-0.02em] text-[#F25C05]">
                    {m.value}
                  </p>
                  <p className="text-white/80 text-sm mt-2 leading-snug">
                    {m[`label${L}`]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="mt-20 md:mt-28" data-testid="about-values-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 md:mb-14">
            <div className="lg:col-span-5">
              <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#F25C05]">
                {t('valuesEyebrow')}
              </p>
              <h2 className="font-display font-bold text-[#05223D] text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.02] mt-3">
                {t('valuesTitle')}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {VALUES.map((v) => {
              const Icon = v.icon;
              return (
                <div
                  key={v.key}
                  className="card-soft p-7 hover:-translate-y-1 hover:shadow-[0_18px_44px_rgba(10,77,140,0.08)] transition-all duration-300"
                  data-testid={`about-value-${v.key}`}
                >
                  <span className="w-11 h-11 rounded-2xl bg-[#F25C05]/10 text-[#F25C05] flex items-center justify-center">
                    <Icon size={20} strokeWidth={2} />
                  </span>
                  <h3 className="font-display font-bold text-[#05223D] text-xl tracking-tight mt-4">
                    {v[`title${L}`]}
                  </h3>
                  <p className="text-sm text-[#465B70] mt-2 leading-relaxed">
                    {v[`body${L}`]}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Team */}
        <section className="mt-20 md:mt-28" data-testid="about-team-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 md:mb-14">
            <div className="lg:col-span-7">
              <p className="text-xs tracking-[0.22em] uppercase font-bold text-[#F25C05]">
                {t('teamEyebrow')}
              </p>
              <h2 className="font-display font-bold text-[#05223D] text-4xl sm:text-5xl tracking-[-0.02em] leading-[1.02] mt-3">
                {t('teamTitle')}
              </h2>
              <p className="text-[#465B70] text-lg leading-relaxed mt-5 max-w-2xl">
                {t('teamIntro')}
              </p>
            </div>
          </div>
          {/* 2×2 on narrow screens, 4×1 on desktop */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 md:gap-6">
            {TEAM.map((m) => (
              <div
                key={m.key}
                className="card-soft overflow-hidden"
                data-testid={`about-team-${m.key}`}
              >
                <div className="aspect-[5/6] overflow-hidden bg-[#F5F2EB]">
                  <img
                    src={m.image}
                    alt={m[`name${L}`]}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-3.5 sm:p-5 md:p-6">
                  <p className="text-[10px] sm:text-xs tracking-[0.22em] uppercase font-bold text-[#F25C05]">
                    {m[`role${L}`]}
                  </p>
                  <h3 className="font-display font-bold text-[#05223D] text-base sm:text-xl tracking-tight mt-1 sm:mt-1.5">
                    {m[`name${L}`]}
                  </h3>
                  <p className="text-[12.5px] sm:text-sm text-[#465B70] mt-1.5 sm:mt-2 leading-relaxed">
                    {m[`bio${L}`]}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* NOTE: The closing CTA block ("SmartPaw Food — Register / Book Free
            Consultation") is rendered automatically by PageShell for every
            inner page, so we don't duplicate it here. */}
      </PageShell>

      <ConsultationDialog open={consultOpen} onOpenChange={setConsultOpen} />
    </>
  );
}
